import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, FileText, Shield, Fuel, DollarSign, Users, Monitor, Megaphone, MapPin, Mail, Phone, ExternalLink, PlayCircle, GraduationCap, Eye, MessageCircle, TrendingUp } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";

interface StatItemProps {
  icon: React.ElementType;
  endValue: number;
  suffix?: string;
  label: string;
  prefix?: string;
  decimals?: boolean;
}

function StatItem({ icon: Icon, endValue, suffix = "", prefix = "", label, decimals = false }: StatItemProps) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const duration = 1800;
    const steps = 60;
    const increment = endValue / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      // easeOutQuart
      const eased = 1 - Math.pow(1 - progress, 4);
      start = eased * endValue;
      setCount(Math.floor(start));
      if (step >= steps) { setCount(endValue); clearInterval(timer); }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, endValue]);

  return (
    <div ref={ref} className="group flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-2 sm:px-4 text-center sm:text-left cursor-default">
      <div className="relative">
        <div className="absolute -inset-2 rounded-full bg-primary/20 scale-0 group-hover:scale-100 transition-transform duration-500 blur-sm" />
        <Icon className="w-10 h-10 sm:w-12 sm:h-12 text-primary relative z-10 transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-[0_0_10px_rgba(59,130,246,0.8)]" />
      </div>
      <div>
        <div className="text-xl sm:text-3xl font-bold text-white tabular-nums tracking-tight transition-all duration-300">
          {prefix}{count.toLocaleString()}{suffix}
        </div>
        <div className="text-[10px] sm:text-sm text-white/80 font-medium tracking-wide group-hover:text-white transition-colors duration-300">{label}</div>
      </div>
    </div>
  );
}

function MissionItem({ item, index, t }: { item: any, index: number, t: any }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div 
      ref={ref} 
      className={`flex flex-col items-center text-center px-4 transition-all duration-700 ease-out transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="mb-4 lg:mb-6 text-primary transition-all duration-500 hover:scale-110 hover:rotate-3 group cursor-default">
        <div className="relative">
          <div className="absolute -inset-4 bg-primary/5 rounded-full scale-0 group-hover:scale-100 transition-transform duration-500" />
          <item.icon className="w-12 h-12 relative z-10" strokeWidth={1.5} />
        </div>
      </div>
      <h3 className="text-base lg:text-sm font-bold text-navy-950 mb-2 lg:mb-3">{t(`mission.items.${item.itemIndex}.title`)}</h3>
      <p className="text-sm lg:text-xs text-slate-500 leading-relaxed max-w-[220px] lg:max-w-[180px]">{t(`mission.items.${item.itemIndex}.desc`)}</p>
    </div>
  );
}

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "AITIM Group",
  "image": "https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp",
  "logo": "https://vibe.filesafe.space/1777392978847194987/attachments/7e47838b-fe62-45a6-9466-14b47e07dd0c.webp",
  "@id": "https://aitimgroup.com",
  "url": "https://aitimgroup.com",
  "telephone": "+17739809090",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "1114 N Arlington Heights Rd Suite 201",
    "addressLocality": "Arlington Heights",
    "addressRegion": "IL",
    "postalCode": "60004",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 42.0945,
    "longitude": -87.9806
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "09:00",
    "closes": "17:00"
  },
  "sameAs": [
    "https://www.facebook.com/aitiminc/",
    "https://www.instagram.com/aitim_group/",
    "https://www.linkedin.com/company/aitim"
  ]
};

const Index = () => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title={t('hero.title1') + ' ' + t('hero.title2') + ' ' + t('hero.title3')}
        description={t('hero.desc')}
        canonical="/"
        schemaJson={localBusinessSchema}
      />
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-28 pb-16 lg:pt-48 lg:pb-32 overflow-hidden bg-navy-950">
          {/* Background Image & Overlay */}
          <div className="absolute inset-0 z-0 bg-navy-950">
            {/* Mobile Image */}
            <img 
              src="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp?w=768" 
              srcSet="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp?w=480 480w, https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f0ff9b40534248c5a223af.webp?w=768 768w"
              sizes="(max-width: 768px) 100vw, 768px"
              alt="AITIM Group Trucking Services" 
              crossOrigin="anonymous"
              fetchPriority="high"
              width={768}
              height={512}
              className="w-full h-full object-cover object-center md:hidden"
              loading="eager"
            />
            {/* Desktop Video */}
            {!isMobile && (
              <video 
                autoPlay 
                loop 
                muted 
                playsInline 
                crossOrigin="anonymous"
                className="w-full h-full object-cover object-center hidden md:block"
              >
                <source src="https://assets.cdn.filesafe.space/gUCbLLrJwD5vtwXYoPEZ/media/69f127e8c428703a042e614e.webm" type="video/webm" />
              </video>
            )}
            <div className="absolute inset-0 bg-gradient-to-r from-navy-950/95 via-navy-950/80 to-transparent"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary font-semibold text-sm mb-6 border border-primary/30 tracking-wide uppercase">
                {t('hero.badge')}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight">
                {t('hero.title1')} <br/>{t('hero.title2')} <br/>
                <span className="text-primary">{t('hero.title3')}</span>
              </h1>
              <p className="text-lg lg:text-xl text-white/80 mb-10 max-w-2xl leading-relaxed">
                {t('hero.desc')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/free-consultation">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-14 text-lg w-full sm:w-auto">
                    {t('hero.btn1')} <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-sm px-8 h-14 text-lg w-full sm:w-auto relative z-20"
                  onClick={() => {
                    const el = document.getElementById('services-section');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  {t('hero.btn2')} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="bg-navy-900 border-y border-white/10 py-10 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.07)_0%,transparent_70%)] pointer-events-none" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0 md:divide-x divide-white/10">
              <StatItem icon={ShieldCheck} endValue={10} suffix="+" label={t('stats.exp')} />
              <StatItem icon={Fuel} endValue={7000} suffix="+" label={t('stats.trucks')} />
              <StatItem icon={Monitor} endValue={20} suffix="+" label={t('stats.awards')} />
              <StatItem icon={Users} endValue={100} suffix="%" label={t('stats.sat')} />
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services-section" className="py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <div className="text-primary font-bold tracking-widest uppercase text-xs mb-4">{t('services.badge')}</div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-navy-950">
                {t('services.title1')}<br/>
                {t('services.title2')} <span className="text-primary">{t('services.title3')}</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Service Cards */}
              {[
                { icon: ShieldCheck, itemIndex: 0, link: "/safety-compliance" },
                { icon: FileText, itemIndex: 1, link: "/consulting" },
                { icon: Shield, itemIndex: 2, link: "/truck-insurance" },
                { icon: Fuel, itemIndex: 3, link: "/fuel-cards" },
                { icon: DollarSign, itemIndex: 4, link: "/factoring" },
                { icon: Monitor, itemIndex: 6, link: "/tms" },

              ].map((service, i) => (
                <div key={i} className="group bg-white rounded-xl p-8 shadow-sm border border-slate-100 hover:-translate-y-2 hover:shadow-2xl hover:shadow-primary/10 hover:border-primary/20 transition-all duration-500 ease-out flex flex-col relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="flex items-center gap-4 mb-4 relative z-10">
                    <service.icon className="w-10 h-10 text-primary shrink-0 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3" strokeWidth={2} fill="none" />
                    <h3 className="text-lg font-bold text-navy-950 leading-tight">{t(`services.items.${service.itemIndex}.title`)}</h3>
                  </div>
                  <p className="text-slate-500 mb-8 text-sm leading-relaxed flex-grow relative z-10">{t(`services.items.${service.itemIndex}.desc`)}</p>
                  <Link to={service.link || "#"} className="text-primary font-semibold flex items-center text-sm transition-opacity mt-auto relative z-10" aria-label={`${t('services.learnMore')} ${t(`services.items.${service.itemIndex}.title`)}`}>
                    {t('services.learnMore')} <span className="sr-only"> {t(`services.items.${service.itemIndex}.title`)}</span> <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-24 bg-navy-950 text-white relative overflow-hidden">
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-16">
              <div className="text-blue-400 font-bold tracking-wider uppercase text-sm mb-3">{t('process.badge')}</div>
              <h2 className="text-4xl font-bold">{t('process.title')}</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-4 pt-10 md:pt-20 pb-12">
              {[
                { num: "01", icon: FileText, itemIndex: 0 },
                { num: "02", icon: ShieldCheck, itemIndex: 1 },
                { num: "03", icon: Mail, itemIndex: 2 },
                { num: "04", icon: Monitor, itemIndex: 3 },
                { num: "05", icon: Users, itemIndex: 4 },
                { num: "06", icon: FileText, itemIndex: 5 },
              ].map((step, i) => (
                <div key={i} className="relative group mt-6 md:mt-0">
                  {/* Large background number that appears above */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none select-none z-0 opacity-0 md:group-hover:opacity-100 transition-all duration-500 translate-y-8 md:group-hover:-translate-y-20 hidden md:flex flex-col items-center">
                    <span className="text-[80px] font-black text-white leading-none">
                      {parseInt(step.num, 10)}
                    </span>
                    <div className="w-14 h-2 bg-white mt-2"></div>
                  </div>

                  {/* The Card */}
                  <div className="relative z-10 p-6 border border-white/10 rounded-xl bg-slate-900 transition-all duration-500 ease-out md:group-hover:translate-y-12 md:group-hover:shadow-2xl md:group-hover:shadow-black/50 text-center h-full flex flex-col">
                    {/* Small top badge */}
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xs z-20 shadow-lg">
                      {step.num}
                    </div>

                    {/* Content */}
                    <div className="mt-4 mb-5 flex justify-center">
                      <step.icon className="w-10 h-10 text-primary" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-base font-bold mb-3 text-white">{t(`process.items.${step.itemIndex}.title`)}</h3>
                    <p className="text-xs text-white/80 leading-relaxed flex-grow">{t(`process.items.${step.itemIndex}.desc`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <div className="text-primary font-bold tracking-wider uppercase text-sm mb-3">{t('mission.badge')}</div>
              <h2 className="text-4xl font-bold text-navy-950">{t('mission.title')}</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 lg:gap-0 lg:divide-x lg:divide-slate-200">
              {[
                { icon: ShieldCheck, itemIndex: 0 },
                { icon: GraduationCap, itemIndex: 1 },
                { icon: Eye, itemIndex: 2 },
                { icon: MessageCircle, itemIndex: 3 },
                { icon: TrendingUp, itemIndex: 4 },
              ].map((item, i) => (
                <MissionItem key={i} item={item} index={i} t={t} />
              ))}
            </div>
          </div>
        </section>

        {/* Community Section */}
        <section className="bg-navy-950 py-20 text-white">
          <div className="container mx-auto px-4">
            <div className="bg-navy-900 border border-white/10 rounded-2xl overflow-hidden flex flex-col lg:flex-row">
              <div className="p-10 lg:p-16 lg:w-1/2 flex flex-col justify-center">
                <div className="text-blue-400 font-bold tracking-wider uppercase text-sm mb-4">{t('community.badge')}</div>
                <h2 className="text-3xl lg:text-4xl font-bold mb-6 leading-tight">
                  {t('community.title1')}<br/>
                  {t('community.title2')}
                </h2>
                <p className="text-white/70 mb-8 leading-relaxed max-w-lg">
                  {t('community.desc')}
                </p>
                <div>
                  <Link to="/media">
                    <Button className="bg-primary hover:bg-primary/90 text-white rounded-sm px-6 h-12">
                      {t('community.btn')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="lg:w-1/2 flex flex-col sm:grid sm:grid-cols-2 gap-4 p-4 lg:p-2 mt-8 lg:mt-0">
                <img src="https://vibe.filesafe.space/1777392978847194987/attachments/2c501f70-e606-4e4e-926f-02240c4a2eb7.webp?w=600" crossOrigin="anonymous" alt="Media production" width={600} height={400} loading="lazy" className="w-full h-64 sm:h-full object-cover rounded-lg" />
                <div className="flex flex-col sm:grid sm:grid-rows-2 gap-4">
                  <img src="https://vibe.filesafe.space/1777392978847194987/attachments/36b89c2b-7e8d-4bc9-8473-cab39d86eae4.webp?w=600" crossOrigin="anonymous" alt="Awards ceremony" width={600} height={400} loading="lazy" className="w-full h-64 sm:h-full object-cover rounded-lg" />
                  <div className="bg-navy-950 rounded-lg p-6 flex flex-col justify-center items-center text-center border border-white/10">
                    <div className="text-4xl font-bold text-primary mb-2">10</div>
                    <div className="text-sm font-bold text-white mb-3 uppercase tracking-wider">{t('community.awards')}</div>
                    <p className="text-xs text-white/80 mb-4">{t('community.awardsDesc')}</p>
                    <Link to="/awards" className="text-xs font-medium text-white hover:text-primary transition-colors flex items-center" aria-label={`${t('community.learnMore')} ${t('nav.awards')}`}>
                      {t('community.learnMore')} <span className="sr-only"> {t('nav.awards')}</span> <ArrowRight className="ml-1 w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-24 overflow-hidden bg-navy-950">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://vibe.filesafe.space/1777392978847194987/attachments/0169c122-f8bd-4cc8-b79e-155a815cef61.webp?w=1200" 
              alt="Truck on highway" 
              crossOrigin="anonymous"
              loading="lazy"
              width={1200}
              height={675}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/90 to-navy-950/40"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10 flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="max-w-2xl">
              <h2 className="text-4xl font-bold text-white mb-4">{t('cta.title')}</h2>
              <p className="text-lg text-white/80">
                {t('cta.desc')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0 w-full lg:w-auto">
              <Link to="/free-consultation">
                <Button size="lg" className="bg-white text-navy-950 hover:bg-white/90 rounded-sm px-8 h-14 text-lg font-semibold w-full sm:w-auto">
                  {t('cta.btn1')}
                </Button>
              </Link>
              <a href="tel:7739809090" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-sm px-8 h-14 text-lg w-full">
                  <Phone className="mr-2 w-5 h-5" /> {t('cta.btn2')} 773-980-9090
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
